import React, { useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useTheme } from '../contexts/ThemeContext';

const FONT = "'Segoe UI', system-ui, Roboto, Helvetica, Arial, sans-serif";
const ACCENT = '#c24141';
const ACCENT_STRONG = '#b33333';

export function createAppTheme(darkMode) {
  return createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: ACCENT,
        dark: ACCENT_STRONG,
        contrastText: '#ffffff',
      },
      secondary: {
        main: darkMode ? '#9a9a9a' : '#6b6b6b',
      },
      background: {
        default: darkMode ? '#141414' : '#f4f4f5',
        paper: darkMode ? '#1c1c1c' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#ececec' : '#1a1a1a',
        secondary: darkMode ? '#9a9a9a' : '#6b6b6b',
      },
      divider: darkMode ? '#2e2e2e' : '#e4e4e7',
      success: { main: '#3d8b4a' },
      error: { main: '#c24141' },
    },
    typography: {
      fontFamily: FONT,
      fontSize: 15,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em' },
      h2: { fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' },
      h3: { fontSize: 18, fontWeight: 600 },
      body1: { fontSize: 15, fontWeight: 400 },
      body2: { fontSize: 14, fontWeight: 400 },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 15,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: Array(25).fill('none'),
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: FONT,
            backgroundColor: darkMode ? '#141414' : '#f4f4f5',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            minHeight: 44,
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          containedPrimary: {
            backgroundColor: ACCENT,
            '&:hover': { backgroundColor: ACCENT_STRONG },
          },
          outlined: {
            borderColor: darkMode ? '#2e2e2e' : '#e4e4e7',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#171717' : '#f4f4f5',
            '&:not(.MuiInputBase-sizeSmall)': {
              minHeight: 56,
            },
            '& fieldset': {
              borderColor: darkMode ? '#3a3a3a' : '#d4d4d8',
            },
            '&:hover fieldset': {
              borderColor: darkMode ? '#555' : '#a1a1aa',
            },
            '&.Mui-focused fieldset': {
              borderColor: `${ACCENT} !important`,
              borderWidth: '1px',
            },
          },
          input: {
            fontSize: 15,
            fontWeight: 400,
            lineHeight: 1.5,
            padding: '15px 14px',
            boxSizing: 'content-box',
            height: '1.5em',
          },
          inputSizeSmall: {
            fontSize: 14,
            lineHeight: 1.4375,
            padding: '8.5px 14px',
            height: '1.4375em',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 400,
            fontSize: 15,
            lineHeight: 1.2,
            overflow: 'visible',
          },
          sizeSmall: {
            fontSize: 14,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '&.MuiInputBase-inputSizeSmall': {
              paddingRight: 32,
            },
            '&:not(.MuiInputBase-inputSizeSmall)': {
              paddingRight: 40,
            },
          },
          icon: {
            right: 8,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: 14,
            fontWeight: 400,
            borderColor: darkMode ? '#2e2e2e' : '#e4e4e7',
          },
          head: {
            fontSize: 13,
            fontWeight: 600,
            color: darkMode ? '#9a9a9a' : '#6b6b6b',
            backgroundColor: darkMode ? '#191919' : '#fafafa',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: darkMode ? '#242424' : 'rgba(0,0,0,0.03)',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 3,
            borderRadius: 0,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 40,
            color: 'inherit',
          },
        },
      },
    },
  });
}

export function AppMuiTheme({ children }) {
  const { darkMode } = useTheme();
  const theme = useMemo(() => createAppTheme(darkMode), [darkMode]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}

const theme = createAppTheme(false);
export default theme;
